import { Cluster, MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { t } from "@ebay/i18n-ebay";
import { setAttrsForClickTracking } from "../../utils/tracking";
import { trackingDataCluster, trackingDataClusterStacked } from "../map-cluster/tracking";
import type { MapViewModule, RequestedListing } from "../../@types/mapviewxs";
import { getPreviewCardPosition } from "../../utils/preview-card-position";
import { focusOnSelectedMarkerPreview } from "../../utils/accessibility";
import { areMarkersInSameCoordinates, calculateMinMaxLatitude, getVisiblePointsOfInterest, isSameBound } from "./helpers";
import { highlightMarker, renderMarker, selectMarker, unHighlightMarkers, unselectMarkers } from "./marker";
import { createMap } from "./map";
import { loadLibraries } from "./libraries";
import { drawCircle } from "./circle";
import { clusterRenderer, createActiveClusterComponent, createClusterComponent, createClusterItems, findClusterGroup } from "./cluster";
import type { Input, LatLngBounds, MarkerSelectionState, MarkerWithCircle, State } from "./types";
import { DEFAULT_CLUSTER_RADIUS, DEFAULT_EXTENT, DEFAULT_MAP_PADDING, MAX_ZOOM_LEVEL, SHOW_CIRCLES_ZOOM_LEVEL } from "./const";
import { loadListings, loadMore } from "./loadmore";
import { onMessage, sendMapInitToParentWindow, sendModelToParentWindow } from "./post-message";
import { getMarkersBounds, moveMapToBounds } from "./helpers/bounds";
import { initialState, stateFromModel } from "./helpers/state";

export default class extends Marko.Component<Input, State> {
  declare mapsLibrary: google.maps.MapsLibrary | undefined;
  declare markerLibrary: google.maps.MarkerLibrary | undefined;
  declare map: google.maps.Map | undefined;
  declare mapEl: HTMLElement | undefined;
  declare points: MarkerWithCircle[];
  declare markerCluster: MarkerClusterer | null;
  declare currentBounds: LatLngBounds | undefined;
  declare currentZoom: number | undefined;
  declare minLatitude: number | null;
  declare maxLatitude: number | null;
  declare selectedCluster: Cluster | null;
  declare highlightedCluster: Cluster | null;
  declare leftRailLoadingTimeout: NodeJS.Timeout | undefined;
  declare selectedMarkerWithPreview: MarkerWithCircle | null;

  onCreate({ model }: Input) {
    this.state = {
      ...initialState,
      ...stateFromModel(model),
    } as State;
    this.mapsLibrary = undefined;
    this.markerLibrary = undefined;
    this.map = undefined;
    this.mapEl = undefined;
    this.points = [];
    this.markerCluster = null;
    this.currentBounds = undefined;
    this.currentZoom = undefined;
    this.minLatitude = null;
    this.maxLatitude = null;
    this.selectedCluster = null;
    this.highlightedCluster = null;
    this.leftRailLoadingTimeout = undefined;
    this.selectedMarkerWithPreview = null;
  }

  async onMount() {
    const { mapsLibrary, markerLibrary } = await loadLibraries(this.input.apiKey);
    this.mapsLibrary = mapsLibrary;
    this.markerLibrary = markerLibrary;

    const { model, isRecommendationsView, listingsView, isMobile, isPreviewCardEnabled, dialogTitle, dialogSubtitle } = this.input || {};

    // Check if the page is rendered inside an iframe
    if (window !== window.top) {
      // Page is loaded in iframe
      if (isRecommendationsView) {
        this.onRecommendationsView();
        return;
      }

      const defaultIntitialModel = {
        title: { textSpans: [{ text: dialogTitle }] },
        subtitle: { textSpans: [{ text: dialogSubtitle }] },
      };

      sendModelToParentWindow(model || defaultIntitialModel);
      sendMapInitToParentWindow();
      this.initSetListingsHandler();
    } else {
      this.onRecommendationsView();
    }

    this.state.isPreviewCardEnabled = Boolean(isPreviewCardEnabled && listingsView === "left-rail" && !isMobile);
  }

  // Recommendations flow
  async onRecommendationsView() {
    await this.initMap();
    this.state.isLoading = false;

    if (this.input?.waitingForService) return;

    this.addMarkersAndClusters();
  }

  async initMap() {
    this.mapEl = this.getEl("map");

    const { mapId, isMobile, marketplaceId } = this.input;

    const map = createMap({
      mapId,
      mapsLibrary: this.mapsLibrary!,
      mapEl: this.mapEl,
      isMobile,
      marketplaceId,
    });

    if (!map) throw Error("Map failed to load");

    map.addListener("click", this.onMapClick.bind(this));
    map.addListener("bounds_changed", this.onMapBoundChange.bind(this));
    map.addListener("zoom_changed", this.onZoomChanged.bind(this));
    map.addListener("idle", this.onMapBoundChangeDebounced.bind(this));
    map.addListener("dragstart", () => this.mapEl?.classList.add("map-dragging"));
    map.addListener("dragend", () => this.mapEl?.classList.remove("map-dragging"));

    this.map = map;
  }

  initSetListingsHandler() {
    onMessage("map-view-set-listings", async (event: MessageEvent) => {
      try {
        if ((event.data?.listings || []).length > 0) {
          await this.setListings(event.data.listings);
        }
      } catch {
        this.state.showErrorMessage = true;
      } finally {
        this.state.isLoading = false;
      }
    });
  }

  async setListings(listings: RequestedListing[]) {
    try {
      const [listingsResult] = await Promise.all([
        loadListings({
          listings: listings,
          csrfToken: this.input.csrfToken,
        }),
        this.map || this.initMap(),
      ]);
      // Workaround: XS returns no model (POI, help) if request query doesn't contain `listings` or `sources`
      sendModelToParentWindow(listingsResult);
      this.onLoadMoreSuccessHandler({ model: listingsResult, method: "post" });
    } catch {
      const fallbackModel = {
        title: { textSpans: [{ text: t("mapview/map", "errorTitle") }] },
      };
      sendModelToParentWindow(fallbackModel);
    }
  }

  addMarkersAndClusters(isLoadMore?: boolean) {
    const { minLatitude, maxLatitude } = calculateMinMaxLatitude(this.state.pointsOfInterest);
    this.minLatitude = minLatitude;
    this.maxLatitude = maxLatitude;

    this.addMarkers(isLoadMore);
    this.addClusters();
  }

  onDestroy() {
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map);
    }
  }

  onMapClick() {
    this.state.isLeftRailItemHover = false;
    this.clearHighlightedMarkers();
    this.clearSelectedMarkers();
  }

  setSelectedMarkerCircle(selectedMarkerIndex: number) {
    const zoomLevel = this.map?.getZoom();
    if (zoomLevel && zoomLevel < SHOW_CIRCLES_ZOOM_LEVEL) return;
    if (typeof selectedMarkerIndex !== "number") return;
    const selectedMarkerCircle = this.points[selectedMarkerIndex];
    if (selectedMarkerCircle && !selectedMarkerCircle.circle) {
      const point = this.state.pointsOfInterest?.[selectedMarkerIndex];
      if (!point?.geometry?.circle) return;
      this.points[selectedMarkerIndex].circle = drawCircle(this.map!, point.geometry.circle);
    }
  }

  clearSelectedMarkerCircle(selectedMarkerIndex: number) {
    if (typeof selectedMarkerIndex !== "number") return;
    const selectedMarkerCircle = this.points?.[selectedMarkerIndex];
    if (selectedMarkerCircle.circle) {
      this.points[selectedMarkerIndex].circle!.setMap(null);
      this.points[selectedMarkerIndex].circle = undefined;
    }
  }

  updateMarkerCircleWithZoomLevel(markerIndex: number | null) {
    const zoomLevel = this.map?.getZoom();
    const shouldShowMarkerCircle = zoomLevel && zoomLevel > SHOW_CIRCLES_ZOOM_LEVEL;
    if (markerIndex !== null && this.points[markerIndex]) {
      if (shouldShowMarkerCircle) {
        this.setSelectedMarkerCircle(markerIndex);
      } else {
        this.clearSelectedMarkerCircle(markerIndex);
      }
    }
  }

  clearAndAddSelectedMarkerWithPreview(markerIndex: number, cluster?: Cluster) {
    if (typeof markerIndex !== "number" || this.selectedMarkerWithPreview) return;

    const isStackedItems = this.state.selectedItems.length > 1 && cluster;
    const mapElement = document.querySelector("#map");
    const previewItemElement = document.querySelector(".preview-item");

    if (!mapElement || !previewItemElement) {
      console.error("Map or preview element not found");
      return;
    }
    if (isStackedItems) {
      this.createStackedMarkerWithPreview(cluster, mapElement, previewItemElement);
      if (this.selectedMarkerWithPreview) {
        (this.selectedMarkerWithPreview as MarkerWithCircle).marker.addListener("click", () => {});
        focusOnSelectedMarkerPreview(this.selectedMarkerWithPreview);
      }
    } else {
      this.setMarkerToSelected(markerIndex);
    }

    this.updateMarkerCircleWithZoomLevel(markerIndex);
  }

  createStackedMarkerWithPreview(cluster: Cluster, mapElement: Element, previewItemElement: Element) {
    const selectMarkersFromPoints = this.state.selectedItems.map((index) => this.points[index].marker);
    const markerElement = (<google.maps.marker.AdvancedMarkerElement>cluster.marker!).element;
    const position = getPreviewCardPosition(mapElement, markerElement, previewItemElement);

    this.selectedMarkerWithPreview = renderMarker({
      map: this.map!,
      markerLibrary: this.markerLibrary!,
      points: createClusterItems(cluster.markers, this.state.pointsOfInterest),
      selectionState: "selected",
      isPreviewCardEnabled: this.state.isPreviewCardEnabled,
      handlePreviewClose: this.clearSelectedMarkers.bind(this),
      defaultPreviewPosition: position.horizontalPosition ? position : undefined,
      itemCardControls: this.state.itemCardControls,
      mapAccessibilityTexts: this.state.mapAccessibilityTexts,
    });

    this.markerCluster?.removeMarkers(selectMarkersFromPoints);
  }

  updateLeftRailItems() {
    if (this.input.listingsView !== "left-rail" || this.input.isMobile) return;

    const bounds = this.map?.getBounds();
    if (!bounds) return;

    const updatedLeftRailItems = getVisiblePointsOfInterest(this.state.pointsOfInterest, this.points, bounds);
    const isLeftRailItemsChanged = this.state.leftRailItems.length > 0 && this.state.leftRailItems.length !== updatedLeftRailItems.length;
    this.state.leftRailItems = updatedLeftRailItems;

    if (isLeftRailItemsChanged) {
      this.state.isLeftRailItemHover = true;
      this.state.isLoading = true;
      clearTimeout(this.leftRailLoadingTimeout);
      this.leftRailLoadingTimeout = setTimeout(() => {
        this.state.isLoading = false;
      }, 250);
    }
  }

  onMapBoundChangeDebounced() {
    this.updateLeftRailItems();
    const visiblePointsOfInterest = getVisiblePointsOfInterest(this.state.pointsOfInterest, this.points, this.map?.getBounds());
    this.state.isEmptyMapTrackerVisible = !visiblePointsOfInterest.length;
  }

  onMapBoundChange() {
    this.updateMarkerCircleWithZoomLevel(this.state.selectedItems[0]);
    this.updateMarkerCircleWithZoomLevel(this.state.highlightedItem);

    if (!this.currentBounds) {
      this.currentBounds = this.map?.getBounds();
      return;
    }

    this.state.showErrorMessage = false;
    if (this.map?.getBounds()?.equals(this.currentBounds) || this.state.isLoadMoreVisible) return;

    this.state.isLoadMoreVisible = !this.state.isLoadMoreVisible;
    this.currentBounds = this.map?.getBounds();
  }

  onZoomChanged() {
    if (!this.currentBounds || !this.currentZoom) {
      this.currentZoom = this.map?.getZoom();
      return;
    }

    if (this.state.highlightedItem) {
      this.clearHighlightedMarkers();
    }

    const isZoomed = this.map?.getZoom() !== this.currentZoom;
    if (this.state.isPreviewCardEnabled && this.state.selectedItems.length === 1 && isZoomed) {
      this.clearAndAddSelectedMarkerWithPreview(this.state.selectedItems[0]);
    }
  }

  addMarkers(isLoadMore?: boolean) {
    if (this.state.pointsOfInterest?.length === 0) return;

    this.points = this.state.pointsOfInterest
      .map((point, index) => {
        const { marker, component } = renderMarker({
          map: this.map!,
          markerLibrary: this.markerLibrary!,
          points: [point],
          selectionState: "unselected",
          isPreviewCardEnabled: this.state.isPreviewCardEnabled,
          handlePreviewClose: this.clearSelectedMarkers.bind(this),
          itemCardControls: this.state.itemCardControls,
          mapAccessibilityTexts: this.state.mapAccessibilityTexts,
        });

        if (!marker || !component) return;

        marker.id = index.toString();
        setAttrsForClickTracking(marker.element, point.trackingList);
        marker.addListener("click", () => this.onMarkerClick(index));
        return { marker, component };
      })
      .filter(Boolean) as MarkerWithCircle[];

    if (this.points.length && !isLoadMore) {
      this.map?.fitBounds(getMarkersBounds(this.points));
    }
  }

  addExistingMarker() {
    if (this.state.selectedItems.length > 0) {
      const index = this.state.selectedItems[0];
      this.clearSelectedMarkerCircle(index);
      const oldMarker = renderMarker({
        map: this.map!,
        markerLibrary: this.markerLibrary!,
        points: [this.state.pointsOfInterest[index]],
        itemCardControls: this.state.itemCardControls,
        selectionState: "unselected",
        isPreviewCardEnabled: this.state.isPreviewCardEnabled,
        handlePreviewClose: this.clearSelectedMarkers.bind(this),
        mapAccessibilityTexts: this.state.mapAccessibilityTexts,
      });

      oldMarker.marker.addListener("click", () => this.onMarkerClick(index));
      oldMarker.marker.id = index.toString();
      this.markerCluster?.addMarker(oldMarker.marker);
      this.points[index] = oldMarker;
      // To re-run clustering algorithm after adding marker back to cluster
      this.addClusters();
    }
  }
  addExistingCluster() {
    if (this.state.selectedItems.length > 1 && this.selectedCluster?.markers) {
      this.markerCluster?.addMarkers(this.selectedCluster.markers);
    }
  }

  onMarkerClick(markerIndex: number) {
    this.state.isLeftRailItemHover = false;
    this.clearHighlightedMarkers();
    this.clearSelectedMarkers();
    this.clearAndAddSelectedMarkerWithPreview(markerIndex);
  }

  onUnhoverItem() {
    this.clearHighlightedMarkers();
  }

  onItemHover(itemIndex: number, listingId: string) {
    this.state.isLeftRailItemHover = true;
    if (listingId && !this.state.selectedItems.includes(itemIndex)) {
      if (this.markerCluster) {
        const selectedMarker = this.points[itemIndex].marker;
        const clusterGroup = findClusterGroup(this.markerCluster, selectedMarker);
        if (clusterGroup) {
          const cluster = clusterGroup.markers && clusterGroup.markers.length > 1;
          if (cluster) {
            this.toggleHighlightedCluster(clusterGroup);
            this.state.clusterPointsOfInterest = createClusterItems(clusterGroup.markers, this.state.pointsOfInterest) || [];
          }
        }
      }
    }
    this.setMarkerToHighlighted(itemIndex);
  }

  onShowAll() {
    this.clearSelectedMarkers();

    if (this.state.leftRailItems.length < this.state.pointsOfInterest.length) {
      this.state.leftRailItems = this.state.pointsOfInterest;

      this.state.isLoading = true;
      clearTimeout(this.leftRailLoadingTimeout);
      this.leftRailLoadingTimeout = setTimeout(() => {
        this.state.isLoading = false;
      }, 250);

      if (this.points.length) {
        this.map?.fitBounds(getMarkersBounds(this.points));
      }
    }
  }

  clearHighlightedMarkers() {
    this.clearHighlightedCluster();
    unHighlightMarkers(this.points);

    this.state.prevHighlightedItem = this.state.highlightedItem;
    this.state.highlightedItem = null;
  }

  setMarkerToHighlighted(markerIndex: number) {
    const isMarkerHighlighted = this.state.highlightedItem === markerIndex;
    const isMarkerSelected = this.state.selectedItems.includes(markerIndex);
    if (isMarkerHighlighted || isMarkerSelected || this.state.isLoading) return;

    this.state.prevHighlightedItem = this.state.highlightedItem;
    this.state.highlightedItem = markerIndex;

    if (markerIndex === null) return;

    highlightMarker(this.points[markerIndex]);
    this.setSelectedMarkerCircle(markerIndex);
  }

  clearSelectedMarkers() {
    if (this.selectedMarkerWithPreview) {
      this[this.selectedCluster ? "addExistingCluster" : "addExistingMarker"]();
    }
    this.clearUnclusteredItem();
    this.clearSelectedCluster();
    this.clearClusterCircle();

    this.state.prevSelectedItems = this.state.selectedItems;
    this.state.selectedItems = [];
    unselectMarkers(this.points);
  }

  clearUnclusteredItem() {
    if (!this.selectedMarkerWithPreview) return;

    (this.selectedMarkerWithPreview?.marker as google.maps.Marker).setMap(null);
    this.selectedMarkerWithPreview = null;
  }

  setMarkerToSelected(markerIndex: number) {
    // // un-select any markers that are currently selected
    const isMarkerSelected = this.state.selectedItems.includes(markerIndex);
    if (isMarkerSelected || markerIndex === null) return;
    this.clearSelectedCluster();
    unselectMarkers(this.points);
    this.clearClusterCircle();

    this.state.prevSelectedItems = this.state.selectedItems;
    this.state.selectedItems = [markerIndex];
    selectMarker(this.points[markerIndex]);
    focusOnSelectedMarkerPreview(this.points[markerIndex]);
  }

  clearClusterCircle(): void {
    this.clearSelectedMarkerCircle(this.state.selectedItems[0]);
    if (this.state.clusterPointsOfInterest.length === 0) return;
    this.state.clusterPointsOfInterest = [];
  }

  setSelectedItems(itemIndex: number[] = []): void {
    this.state.prevSelectedItems = this.state.selectedItems;
    this.state.selectedItems = itemIndex;
  }

  clearHighlightedCluster(): void {
    if (!this.highlightedCluster) return;
    (<google.maps.marker.AdvancedMarkerElement>this.highlightedCluster.marker)!.content = createClusterComponent({
      count: this.highlightedCluster.count,
      isStackedItems: areMarkersInSameCoordinates(this.highlightedCluster),
      clusterPointsOfInterest: createClusterItems(this.highlightedCluster.markers, this.state.pointsOfInterest),
      selectedMarker: null,
      mapAccessibilityTexts: this.state.mapAccessibilityTexts,
    });
    this.highlightedCluster = null;
  }

  clearSelectedCluster(): void {
    if (!this.selectedCluster) return;
    (<google.maps.marker.AdvancedMarkerElement>this.selectedCluster.marker)!.content = createClusterComponent({
      count: this.selectedCluster.count,
      isStackedItems: areMarkersInSameCoordinates(this.selectedCluster),
      clusterPointsOfInterest: createClusterItems(this.selectedCluster.markers, this.state.pointsOfInterest),
      selectedMarker: null,
      mapAccessibilityTexts: this.state.mapAccessibilityTexts,
    });
    this.selectedCluster = null;
  }

  addClusters() {
    if (this.points?.length === 0) return;

    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
    }

    this.markerCluster = new MarkerClusterer({
      map: this.map,
      markers: this.points.map((point) => point.marker),
      renderer: {
        render: (cluster) => {
          const { count, position, bounds, markers } = cluster;
          // When preview card is enabled, we show a marker with the preview card open instead of a selected cluster
          const selectedCluster = this.state.isPreviewCardEnabled ? null : this.selectedCluster;
          const clusterState: MarkerSelectionState = selectedCluster ? "selected" : this.highlightedCluster ? "highlighted" : "unselected";
          const isStackedItems = areMarkersInSameCoordinates(cluster);
          const rendered = clusterRenderer({
            markerLibrary: this.markerLibrary!,
            count,
            position,
            bounds: bounds!,
            clusterState,
            itemCardControls: this.state.itemCardControls,
            selectedCluster,
            highlightedCluster: this.highlightedCluster,
            isStackedItems,
            clusterPointsOfInterest: createClusterItems(markers, this.state.pointsOfInterest),
            selectedMarker: this.points[this.state.selectedItems[0]],
            mapAccessibilityTexts: this.state.mapAccessibilityTexts,
          });
          const trackingData = isStackedItems ? trackingDataClusterStacked(this.input.entryPageId) : trackingDataCluster(this.input.entryPageId);
          setAttrsForClickTracking(rendered.element, [trackingData]);

          return rendered;
        },
      },
      onClusterClick: this.onClusterClick.bind(this),
      algorithm: new SuperClusterAlgorithm({ maxZoom: MAX_ZOOM_LEVEL, radius: DEFAULT_CLUSTER_RADIUS, extent: DEFAULT_EXTENT }),
    });
  }

  selectAllItemsInStacked(cluster: Cluster) {
    this.state.isLeftRailItemHover = false;
    const selectedItemIds = cluster.markers?.map((marker) => parseInt((marker as any).id)).reverse() || [];
    this.setSelectedItems(selectedItemIds);
  }

  onClusterClick(_: google.maps.MapMouseEvent, cluster: Cluster) {
    if (!cluster.bounds || this.state.isLoading) return;

    const clusterCircle = this.points?.[this.state.selectedItems[0]]?.circle;
    const clusterCircleBounds = clusterCircle?.getBounds();
    const isSameLocation = clusterCircleBounds && isSameBound(cluster.bounds, clusterCircleBounds);
    const isStackedItems = areMarkersInSameCoordinates(cluster);
    const isItemHighlightedSameLocation = isSameLocation && typeof this.state.highlightedItem === "number" && this.input.listingsView === "left-rail";

    if (!isStackedItems && !isSameLocation) {
      moveMapToBounds(this.map!, cluster.bounds, this.mapEl!, DEFAULT_MAP_PADDING);
    }

    if (isStackedItems || isItemHighlightedSameLocation) {
      this.clearSelectedMarkers();
      this.clearHighlightedMarkers();
      this.selectAllItemsInStacked(cluster);
      this.toggleSelectedCluster(cluster);
      this.state.clusterPointsOfInterest = createClusterItems(cluster.markers, this.state.pointsOfInterest) || [];
    }
  }

  toggleHighlightedCluster(cluster: Cluster) {
    if (this.highlightedCluster) {
      (<google.maps.marker.AdvancedMarkerElement>this.highlightedCluster.marker!).content = createClusterComponent({
        count: this.highlightedCluster.count,
        isStackedItems: areMarkersInSameCoordinates(this.highlightedCluster),
        clusterPointsOfInterest: createClusterItems(this.highlightedCluster.markers, this.state.pointsOfInterest),
        selectedMarker: null,
        mapAccessibilityTexts: this.state.mapAccessibilityTexts,
      });
    }
    (<google.maps.marker.AdvancedMarkerElement>cluster.marker!).content = createActiveClusterComponent({
      count: cluster.count,
      selectionState: "highlighted",
      isStackedItems: areMarkersInSameCoordinates(cluster),
      clusterPointsOfInterest: createClusterItems(cluster.markers, this.state.pointsOfInterest),
      selectedMarker: null,
      mapAccessibilityTexts: this.state.mapAccessibilityTexts,
    });
    this.highlightedCluster = cluster;
  }

  toggleSelectedCluster(cluster: Cluster) {
    const firstMarkerInCluster = this.state.selectedItems[0];
    if (this.state.isPreviewCardEnabled) {
      this.clearAndAddSelectedMarkerWithPreview(firstMarkerInCluster, cluster);
    } else {
      (<google.maps.marker.AdvancedMarkerElement>cluster.marker!).content = createActiveClusterComponent({
        count: cluster.count,
        selectionState: "selected",
        isStackedItems: areMarkersInSameCoordinates(cluster),
        clusterPointsOfInterest: createClusterItems(cluster.markers, this.state.pointsOfInterest),
        itemCardControls: this.state.itemCardControls,
        selectedMarker: null,
        mapAccessibilityTexts: this.state.mapAccessibilityTexts,
      });
      this.setSelectedMarkerCircle(firstMarkerInCluster);
    }
    this.selectedCluster = cluster;
  }

  handleZoomOut() {
    const currentZoomLevel = this.map?.getZoom() || 0;
    if (currentZoomLevel > 0) {
      this.map?.setZoom(currentZoomLevel - 1);
    }
  }
  handleZoomIn() {
    const currentZoomLevel = this.map?.getZoom() || 0;
    if (currentZoomLevel < MAX_ZOOM_LEVEL) {
      this.map?.setZoom(currentZoomLevel + 1);
    }
  }

  clearMarkers() {
    for (const point of this.points) {
      (point.marker as any).setMap(null);
      point.component.destroy();
    }
    this.points = [];
  }

  clearMarkerClusters() {
    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
      this.markerCluster.setMap(null);
      this.markerCluster = null;
    }
  }

  clearMap() {
    this.clearSelectedMarkers();
    this.clearHighlightedMarkers();
    this.clearMarkers();
    this.clearMarkerClusters();
    this.clearClusterCircle();
    this.clearSelectedCluster();
  }

  async onLoadMoreClickHandler() {
    this.clearMap();
    this.state.isLoading = true;
    this.state.isLoadingMoreItems = true;
    try {
      const mapview = await loadMore(this.map!, this.input.apiKey);
      this.onLoadMoreSuccessHandler({ model: mapview, method: "get" });
    } catch (error: any) {
      this.state.showErrorMessage = true;
      console.error("Failed on loading more items", error);
    } finally {
      this.state.isLoading = false;
      this.state.isLoadingMoreItems = false;
    }
  }

  onLoadMoreSuccessHandler(options: { model?: MapViewModule; method?: "get" | "post" }) {
    const { model, method } = options;
    if (!model) return;

    if (model.pointsOfInterest?.length === 0) {
      this.state.showErrorMessage = true;
      return;
    }

    this.clearMap();

    this.setState({
      ...(stateFromModel(model) as Partial<this["state"]>),
      isLoadMoreVisible: false,
    });

    const isLoadMore = method === "get";
    this.addMarkersAndClusters(isLoadMore);
  }
}
